import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { TableCell } from '@ovComponents/2-component/table/table';
import { AssetProgressBar } from '@ovComponents/3-pattern/assetProgressBar/assetProgressBar';
import { Box, Typography, useTheme } from 'hephaestus/web';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { portfolioFixedColumnSx } from '../hooks/usePortfolioColumns';
import type { EnrichedPortfolioReport } from '../hooks/usePortfolioTableState.types';
import { SECONDARY_TEXT_SX } from '../styles';
import {
  buildBandConsumedColorTokens,
  computeBandConsumedMetrics,
  getBandConsumedBarFill,
} from '../utils/bandConsumedPercentage';
import { DASH } from '../utils/tableFormatters';

type MetricRowProps = {
  label: string;
  color: string;
  /** Omit to render a dash in place of the percentage/arrow/bar (e.g. no drift data yet). */
  consumedPercentage?: number;
  arrowDirection?: 'up' | 'down';
};

const MetricRow = ({
  label,
  consumedPercentage,
  color,
  arrowDirection,
}: MetricRowProps) => {
  const hasValue = consumedPercentage !== undefined;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 0.5,
          mb: 0.25,
        }}
      >
        <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
          {label}
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.125,
            flexShrink: 0,
          }}
        >
          <Typography
            variant='labelSmall'
            sx={{
              color,
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            {hasValue ? `${Math.round(consumedPercentage)}%` : DASH}
          </Typography>
          {hasValue && (
            <Icon
              icon={
                arrowDirection === 'up'
                  ? KeyboardArrowUpIcon
                  : KeyboardArrowDownIcon
              }
              size='14px'
              sx={{ color }}
            />
          )}
        </Box>
      </Box>
      {hasValue ? (
        <AssetProgressBar
          data={[
            {
              key: label,
              percentage: getBandConsumedBarFill(consumedPercentage),
              colorCode: color,
            },
          ]}
          sx={{ mb: 0.5 }}
        />
      ) : (
        <Typography
          variant='labelSmall'
          component='div'
          sx={{ ...SECONDARY_TEXT_SX, mb: 0.5 }}
        >
          {DASH}
        </Typography>
      )}
    </>
  );
};

export const PortfolioCashPositionCell = React.memo(
  ({
    report,
    driftTolerancePercent,
  }: {
    report: EnrichedPortfolioReport;
    driftTolerancePercent: number;
  }) => {
    const theme = useTheme();
    const { t } = useTranslation('portfolioTradingHub');

    const colorTokens = buildBandConsumedColorTokens(theme);

    const { cash, position, positionDisabledColor } =
      computeBandConsumedMetrics(report, driftTolerancePercent, colorTokens);

    return (
      <TableCell sx={{ ...portfolioFixedColumnSx('cashAndPosition'), py: 0.5 }}>
        <Box sx={{ minWidth: '100%' }}>
          <MetricRow
            label={t('portfoliosTable.cashPosition.cash')}
            consumedPercentage={cash.consumedPercentage}
            color={cash.color}
            arrowDirection={cash.arrowDirection}
          />
          <MetricRow
            label={t('portfoliosTable.cashPosition.position')}
            color={position?.color ?? positionDisabledColor}
            consumedPercentage={position?.consumedPercentage}
            arrowDirection={position?.arrowDirection}
          />
        </Box>
      </TableCell>
    );
  },
);
